import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import UsersPage from './pages/UsersPage';
import MatchesPage from './pages/MatchesPage';
import ChatsPage from './pages/ChatsPage';
import ReportsPage from './pages/ReportsPage';
import AdminManagementPage from './pages/AdminManagementPage';
import FirebaseDataPage from './pages/FirebaseDataPage';
import DatabaseBackupPage from './pages/DatabaseBackupPage';
import AppVersionPage from './pages/AppVersionPage';
import DeletedAccountsPage from './pages/DeletedAccountsPage';
import FeedbackManagementPage from './pages/FeedbackManagementPage';
import ContactManagementPage from './pages/ContactManagementPage';
import AnalyticsPage from './pages/AnalyticsPage';
import NotificationsPage from './pages/NotificationsPage';
import PostsManagementPage from './pages/PostsManagementPage';
import StoriesManagementPage from './pages/StoriesManagementPage';
import GameStatisticsPage from './pages/GameStatisticsPage';
import CommentsManagementPage from './pages/CommentsManagementPage';
import PartnerCafesPage from './pages/PartnerCafesPage';
import FranchisesPage from './pages/FranchisesPage';
import CafeApplicationsPage from './pages/CafeApplicationsPage';
import QRStatisticsPage from './pages/QRStatisticsPage';
import GameManagementPage from './pages/GameManagementPage';
import VSQuestionsManagementPage from './pages/VSQuestionsManagementPage';
import WinnersManagementPage from './pages/WinnersManagementPage';
import RetentionDashboardPage from './pages/RetentionDashboardPage';
import CafeROIDashboardPage from './pages/CafeROIDashboardPage';
import ContentModerationPage from './pages/ContentModerationPage';
import NotificationAnalyticsPage from './pages/NotificationAnalyticsPage';
import PartnersManagementPage from './pages/PartnersManagementPage';
import GiftCardsManagementPage from './pages/GiftCardsManagementPage';
import BeanManagementPage from './pages/BeanManagementPage';
import LetterHintPricingPage from './pages/LetterHintPricingPage';
import LocationMap from './components/LocationMap';
import CafeMapPage from './pages/CafeMapPage';
import MarketingDashboard from './pages/MarketingDashboard';
import ApiHealthMonitorPage from './pages/ApiHealthMonitorPage';
import CafeCRMPage from './pages/CafeCRMPage';
import DailyDashboardPage from './pages/DailyDashboardPage';
import PersonalAgentPage from './pages/PersonalAgentPage';
import LinktreeManagementPage from './pages/LinktreeManagementPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CafeOwnerAuthProvider, useCafeOwnerAuth } from './contexts/CafeOwnerAuthContext';
import CafeOwnerLoginPage from './pages/CafeOwnerLoginPage';
import CafeOwnerDashboardPage from './pages/CafeOwnerDashboardPage';

// Website pages
import HomePage from './pages/website/HomePage';
import ContactPage from './pages/website/ContactPage';
import PrivacyPage from './pages/website/PrivacyPage';
import TermsPage from './pages/website/TermsPage';
import CookiesPage from './pages/website/CookiesPage';
import GDPRPage from './pages/website/GDPRPage';
import HelpPage from './pages/website/HelpPage';
import FeedbackPage from './pages/website/FeedbackPage';
import AppPage from './pages/website/AppPage';
import CafePartnershipPage from './pages/website/CafePartnershipPage';
import PartnershipTermsPage from './pages/website/PartnershipTermsPage';
import QRLandingPage from './pages/website/QRLandingPage';
import MenuPage from './pages/website/MenuPage';
import LinktreePage from './pages/website/LinktreePage';

// Import website styles
import './styles/website.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#8B4513', // Coffee brown
    },
    secondary: {
      main: '#D2691E', // Chocolate
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #8B4513',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#64748b', fontSize: '16px' }}>Yükleniyor...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

const ProtectedCafeOwnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useCafeOwnerAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #8B4513',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#64748b', fontSize: '16px' }}>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/cafe-partnership/login" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Public Website Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/app" element={<AppPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/gdpr" element={<GDPRPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/cafe-partnership" element={<CafePartnershipPage />} />
        <Route path="/partnership-terms" element={<PartnershipTermsPage />} />
        <Route path="/qr/:cafeId" element={<QRLandingPage />} />
        <Route path="/menu/:cafeId" element={<MenuPage />} />
        <Route path="/apps" element={<LinktreePage />} />

        {/* Cafe Owner Panel Routes */}
        <Route path="/cafe-partnership/login" element={<CafeOwnerLoginPage />} />
        <Route path="/cafe-partnership/dashboard" element={
          <ProtectedCafeOwnerRoute>
            <CafeOwnerDashboardPage />
          </ProtectedCafeOwnerRoute>
        } />

        {/* Admin Panel Routes */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <Navigate to="/admin/dashboard" replace />
          </ProtectedRoute>
        } />
        <Route path="/admin/login" element={<LoginPage />} />
        <Route path="/admin/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/matches" element={
          <ProtectedRoute>
            <MatchesPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/chats" element={
          <ProtectedRoute>
            <ChatsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/reports" element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/map" element={
          <ProtectedRoute>
            <LocationMap />
          </ProtectedRoute>
        } />
        <Route path="/admin/admin-management" element={
          <ProtectedRoute>
            <AdminManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/firebase-data" element={
          <ProtectedRoute>
            <FirebaseDataPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/backup" element={
          <ProtectedRoute>
            <DatabaseBackupPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/version" element={
          <ProtectedRoute>
            <AppVersionPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/deleted-accounts" element={
          <ProtectedRoute>
            <DeletedAccountsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/feedback" element={
          <ProtectedRoute>
            <FeedbackManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/contacts" element={
          <ProtectedRoute>
            <ContactManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/notifications" element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/posts" element={
          <ProtectedRoute>
            <PostsManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/stories" element={
          <ProtectedRoute>
            <StoriesManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/game-stats" element={
          <ProtectedRoute>
            <GameStatisticsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/comments" element={
          <ProtectedRoute>
            <CommentsManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/partner-cafes" element={
          <ProtectedRoute>
            <PartnerCafesPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/franchises" element={
          <ProtectedRoute>
            <FranchisesPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/cafe-applications" element={
          <ProtectedRoute>
            <CafeApplicationsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/qr-statistics" element={
          <ProtectedRoute>
            <QRStatisticsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/game-management" element={
          <ProtectedRoute>
            <GameManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/vs-questions" element={
          <ProtectedRoute>
            <VSQuestionsManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/winners-management" element={
          <ProtectedRoute>
            <WinnersManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/retention-dashboard" element={
          <ProtectedRoute>
            <RetentionDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/cafe-roi-dashboard" element={
          <ProtectedRoute>
            <CafeROIDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/content-moderation" element={
          <ProtectedRoute>
            <ContentModerationPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/notification-analytics" element={
          <ProtectedRoute>
            <NotificationAnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/partners" element={
          <ProtectedRoute>
            <PartnersManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/gift-cards" element={
          <ProtectedRoute>
            <GiftCardsManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/beans" element={
          <ProtectedRoute>
            <BeanManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/letter-hint-pricing" element={
          <ProtectedRoute>
            <LetterHintPricingPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/cafe-map" element={
          <ProtectedRoute>
            <CafeMapPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/marketing" element={
          <ProtectedRoute>
            <MarketingDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/api-health" element={
          <ProtectedRoute>
            <ApiHealthMonitorPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/cafe-crm" element={
          <ProtectedRoute>
            <CafeCRMPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/daily-dashboard" element={
          <ProtectedRoute>
            <DailyDashboardPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/my-assistant" element={
          <ProtectedRoute>
            <PersonalAgentPage />
          </ProtectedRoute>
        } />
        <Route path="/admin/linktree" element={
          <ProtectedRoute>
            <LinktreeManagementPage />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <CafeOwnerAuthProvider>
          <AppContent />
        </CafeOwnerAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
